<template>
  <div
    :class="[style.inputBase, rootClass]"
    :data-filled="isFilled"
    :data-error="isError"
    :data-disabled="disabled"
    :data-readonly="readOnly"
    :data-size="size"
  >
    <span
      v-if="description && !showTopError"
      :class="style.description"
      data-input-description
    >
      {{ description }}
    </span>

    <span v-if="showTopError" :class="style.errorText" data-input-error>
      {{ errorText }}
    </span>

    <div
      :class="[
        style.field,
        style[`size${size}`],
        textarea ? style.textareaMode : null
      ]"
      data-input-field
      @click="focusFieldByAreaClick"
    >
      <div :class="style.innerContent">
        <slot name="prefixInput" />

        <input
          v-if="!textarea"
          ref="inputRef"
          v-bind="$attrs"
          :value="displayValue"
          :type="htmlInputType"
          :inputmode="inputMode"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readOnly"
          :aria-invalid="isError || undefined"
          @input="handleInput"
          @blur="handleBlur"
          @focus="handleFocus"
        />

        <textarea
          v-else
          ref="textareaRef"
          v-bind="$attrs"
          :value="modelValue"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readOnly"
          :aria-invalid="isError || undefined"
          :style="textareaStyle"
          rows="1"
          @input="handleInput"
          @blur="handleBlur"
          @focus="handleFocus"
        />
      </div>

      <span v-if="suffix" :class="style.suffix" data-input-suffix>
        {{ suffix }}
      </span>

      <IconBase
        v-if="suffixIcon"
        :class="style.suffixIcon"
        :icon="suffixIcon"
        aria-hidden="true"
        data-input-suffix-icon
      />

      <div v-if="$slots.actions" :class="style.actions" data-input-actions>
        <slot
          name="actions"
          :togglePassword="togglePassword"
          :isPasswordVisible="isPasswordVisible"
          :typeFieldDynamic="typeFieldDynamic"
        />
      </div>
    </div>

    <span
      v-if="showBottomError"
      :class="[style.errorText, style.errorBottom]"
      data-input-error
    >
      {{ errorText }}
    </span>
  </div>
</template>

<script lang="ts">
import {defineComponent, nextTick, PropType} from "vue";
import IconBase from "@/elements/UI/icon/IconBase.vue";
import style from "./InputBase.module.scss";

type InputType = "text" | "password" | "email" | "tel" | "number" | "url";
type ErrorPosition = "top" | "bottom";

export default defineComponent({
  name: "InputBase",

  components: {IconBase},

  inheritAttrs: false,

  emits: ["update:modelValue", "input", "focus", "blur"],

  props: {
    modelValue: {
      type: String,
      default: ""
    },
    size: {
      type: String as PropType<Size>,
      default: "M"
    },
    typeField: {
      type: String as PropType<InputType>,
      default: "text"
    },
    placeholder: {
      type: String,
      default: ""
    },
    description: {
      type: String,
      default: ""
    },
    suffix: {
      type: String,
      default: ""
    },
    suffixIcon: {
      type: String,
      default: ""
    },
    errorText: {
      type: String,
      default: ""
    },
    positionError: {
      type: String as PropType<ErrorPosition>,
      default: "top"
    },
    isError: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    textarea: {
      type: Boolean,
      default: false
    },
    minHeightTextArea: {
      type: Number,
      default: 20
    },
    autoResizeTextarea: {
      type: Boolean,
      default: true
    },
    rootClass: {
      type: [String, Array, Object] as PropType<
        string | string[] | Record<string, boolean>
      >,
      default: ""
    }
  },

  data() {
    return {
      style,
      typeFieldDynamic: this.typeField
    };
  },

  computed: {
    showTopError(): boolean {
      return (
        this.isError && Boolean(this.errorText) && this.positionError === "top"
      );
    },

    showBottomError(): boolean {
      return (
        this.isError &&
        Boolean(this.errorText) &&
        this.positionError === "bottom"
      );
    },

    isFilled(): boolean {
      return this.modelValue.length > 0;
    },

    htmlInputType(): InputType {
      return this.typeField === "tel" ? "tel" : this.typeFieldDynamic;
    },

    inputMode(): "email" | "numeric" | "tel" | "url" | undefined {
      if (this.typeField === "tel") {
        return "tel";
      }

      if (this.typeField === "number") {
        return "numeric";
      }

      if (this.typeField === "email") {
        return "email";
      }

      if (this.typeField === "url") {
        return "url";
      }

      return undefined;
    },

    displayValue(): string {
      return this.typeField === "tel"
        ? this.formatPhoneValue(this.modelValue)
        : this.modelValue;
    },

    textareaStyle(): Record<string, string> {
      return {
        minHeight: `${this.minHeightTextArea}px`
      };
    },

    isPasswordVisible(): boolean {
      return this.typeField === "password" && this.typeFieldDynamic === "text";
    }
  },

  watch: {
    typeField(value: InputType): void {
      this.typeFieldDynamic = value;
    },

    modelValue(): void {
      this.resizeTextarea();
    },

    textarea(): void {
      this.resizeTextarea();
    },

    minHeightTextArea(): void {
      this.resizeTextarea();
    }
  },

  mounted() {
    this.resizeTextarea();
  },

  methods: {
    focusFieldByAreaClick(event: MouseEvent): void {
      if (this.disabled || this.readOnly) {
        return;
      }

      const target = event.target as HTMLElement | null;

      if (!target || this.isInteractiveClickTarget(target)) {
        return;
      }

      const field = this.getFieldElement();

      field?.focus();
    },

    isInteractiveClickTarget(target: HTMLElement): boolean {
      return Boolean(
        target.closest(
          "input, textarea, button, a, select, [role='button'], [data-input-actions]"
        )
      );
    },

    getFieldElement(): HTMLInputElement | HTMLTextAreaElement | null {
      if (this.textarea) {
        return (
          (this.$refs.textareaRef as HTMLTextAreaElement | undefined) ?? null
        );
      }

      return (this.$refs.inputRef as HTMLInputElement | undefined) ?? null;
    },

    handleInput(event: Event): void {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      const value =
        this.typeField === "tel"
          ? this.formatPhoneValue(target.value)
          : target.value;

      this.$emit("update:modelValue", value);
      this.$emit("input", event);

      if (this.typeField === "tel" && target instanceof HTMLInputElement) {
        nextTick(() => {
          target.value = value;
        });
      }

      this.resizeTextarea();
    },

    handleFocus(event: FocusEvent): void {
      this.$emit("focus", event);
    },

    handleBlur(event: FocusEvent): void {
      this.$emit("blur", event);
    },

    togglePassword(): void {
      if (this.typeField !== "password") {
        return;
      }

      this.typeFieldDynamic =
        this.typeFieldDynamic === "password" ? "text" : "password";
    },

    formatPhoneValue(value: string): string {
      const digits = value.replace(/\D/g, "");

      if (!digits) {
        return "";
      }

      const phoneDigits = this.normalizePhoneDigits(digits).slice(0, 11);
      const country = phoneDigits.slice(0, 1);
      const operator = phoneDigits.slice(1, 4);
      const firstPart = phoneDigits.slice(4, 7);
      const secondPart = phoneDigits.slice(7, 9);
      const thirdPart = phoneDigits.slice(9, 11);

      let result = country ? `+${country}` : "";

      if (operator) {
        result += ` ${operator}`;
      }

      if (firstPart) {
        result += ` ${firstPart}`;
      }

      if (secondPart) {
        result += `-${secondPart}`;
      }

      if (thirdPart) {
        result += `-${thirdPart}`;
      }

      return result;
    },

    normalizePhoneDigits(digits: string): string {
      if (digits[0] === "8") {
        return `7${digits.slice(1)}`;
      }

      if (digits[0] === "7") {
        return digits;
      }

      return `7${digits}`;
    },

    resizeTextarea(): void {
      if (!this.textarea || !this.autoResizeTextarea) {
        return;
      }

      nextTick(() => {
        const textarea = this.$refs.textareaRef as
          | HTMLTextAreaElement
          | undefined;

        if (!textarea) {
          return;
        }

        textarea.style.height = "auto";
        textarea.style.height = `${Math.max(
          textarea.scrollHeight,
          this.minHeightTextArea
        )}px`;
      });
    }
  }
});
</script>
